
import { z } from 'zod';
import { leadDetail } from './LeadDetail';
import { newAccountSchema } from './Account';
import { newContactSchema } from './Contact';
import { addressableToPayload } from './Addressable';
import { optionalString } from './OptionalString';
import { CaseMappings } from '@apparatix/utilities/CaseMappings';
import { optionalNumber } from './OptionalNumber';
import { office } from './Office';

export const wizardNewContact = z.object({
  account: newAccountSchema.merge(z.object({
    contactKey: z.string().optional(),
    name: optionalString,
    type: optionalString,
  })).optional().default(() => ({
    name: undefined,
  })),
  contactLabels: z.object({
    contactLabelId: z.number().positive(),
    contactLabelDisplayName: z.string(),
  })
    .array()
    .optional()
    .default(() => []),
  isEditing: z.boolean().optional().default(() => false),
  ...newContactSchema._def.schema.shape,
});

export const newContactPayloadTransformer = wizardNewContact.transform(data => {
  const tmp = { ...data };
  delete tmp.key;
  delete tmp.account;

  const mapper = new CaseMappings();

  const snake: object = mapper.camelToSnake(tmp);

  return {
    ...snake,
    ...addressableToPayload.parse(data),
    // eslint-disable-next-line camelcase
    user_id: data.ownerId,
  };
});

export const leadWizardState = leadDetail.extend({
  name: optionalString,
  notes: z.string().optional(),
  type: z.union([z.literal('Lead'), z.literal('Opportunity')]),
  contacts: wizardNewContact
    .array()
    .min(1, { message: 'Please add a Contact before creating this Lead' })
    .optional()
    .default(() => []),
  accounts: newAccountSchema.array().max(2, { message: 'No more Accounts can be associated with this Lead' })
    .optional()
    .superRefine((values, ctx) => {
      const hasAgency = Boolean(values?.find(account => account.type === 'Agency'));
      const hasAccount = Boolean(values?.find(account => account.type === 'Lead' || account.type === 'Advertiser'));

      if (hasAgency && !hasAccount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please add an Advertiser Account before creating this Lead',
          path: ['accounts'],
        });
      }
    })
    .default(() => []),
});

export const opportunityWizardState = leadDetail.extend({
  name: optionalString,
  notes: z.string().optional(),
  dueDate: z.date().nullable(),
  budget: optionalNumber(),
  type: z.union([z.literal('Lead'), z.literal('Opportunity')]),
  office: office.optional().nullable(),
  contacts: wizardNewContact
    .array()
    .min(1, { message: 'Please add a Contact before creating this Opportunity' })
    .optional()
    .default(() => []),
  accounts: newAccountSchema.array().max(2, { message: 'No more Accounts can be associated with this Opportunity' })
    .min(1, { message: 'Please add an Account before creating this Opportunity' })
    .optional()
    .superRefine((values, ctx) => {
      const hasAgency = Boolean(values?.find(account => account.type === 'Agency'));
      const hasAccount = Boolean(values?.find(account => account.type === 'Lead' || account.type === 'Advertiser'));

      if (hasAgency && !hasAccount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please add an Advertiser Account before creating this Opportunity',
          path: ['accounts'],
        });
      }
    })
    .default(() => []),
});

// Each branch is pinned to its own type: an Opportunity also satisfies the Lead schema, which would
// strip Opportunity-only fields such as dueDate and budget
const unifiedWizardState = z.union([
  leadWizardState.extend({ type: z.literal('Lead') }),
  opportunityWizardState.extend({ type: z.literal('Opportunity') }),
]);

export const transformStateToPayload = unifiedWizardState.transform(data => {
  const tmp = { ...data } as any;
  delete tmp.accounts;
  delete tmp.contacts;
  delete tmp.owner;

  const opportunity = {
    ...tmp,
    accountId: data.accounts?.find(account => account.type === 'Lead' || account.type === 'Advertiser')?.id,
    agencyId: data.accounts?.find(account => account.type === 'Agency')?.id,
    status: data.status?.id,
    temperature: data.temperature?.id,
    ownerId: data.owner?.id,
    source: data.source,
    contacts: data.contacts?.filter(contact => Boolean(contact.id)).map(contact => ({
      id: contact.id,
      labelIds: contact.contactLabels?.map(label => label.contactLabelId),
    })),
  };

  return {
    data: {
      opportunity: Object.fromEntries(
        Object.entries(opportunity).filter(([, value]) => value !== undefined),
      ),
    },
  };
});

export const transformStateToUpdatePayload = unifiedWizardState.transform(data => {
  const tmp = { ...data } as any;
  delete tmp.accounts;
  delete tmp.contacts;
  delete tmp.owner;

  const opportunity = {
    ...tmp,
    id: data.id,
    accountId: data.accounts?.find(account => account.type === 'Lead' || account.type === 'Advertiser')?.id,
    agencyId: data.accounts?.find(account => account.type === 'Agency')?.id,
    status: data.status?.id,
    temperature: data.temperature?.id,
    ownerId: data.owner?.id,
    source: data.source,
    contacts: data.contacts?.filter(contact => Boolean(contact.id)).map(contact => ({
      id: contact.id,
      labelIds: contact.contactLabels?.map(label => label.contactLabelId),
    })),
  };

  return {
    data: {
      opportunity: [Object.fromEntries(
        Object.entries(opportunity).filter(([, value]) => value !== undefined),
      )],
    },
  };
});

export type WizardNewContact = z.infer<typeof wizardNewContact>;
export type LeadWizardState = z.infer<typeof leadWizardState>;
export type OpportunityWizardState = z.infer<typeof opportunityWizardState>;

export type LeadAndOpportunityModel = LeadWizardState & OpportunityWizardState;
